import { animation as animationConfig } from '@config';
import Store from '@store';
import { saveMetadata } from 'src/store/actions/metadata.action';
import { EventContextBridge } from 'src/utils/event-context-bridge';

export class AnimationFactory {
  #animationPreview = null;
  #bridge = null;
  #hasChanges = false;
  #allowedConfigurationKeys = [
    'animateElementsTogether',
    'inOutDuration',
    'pageDuration',
    'applySameDuration',
    'applySameAnimation',
    'minimumPageDuration'
  ];

  #supportedEvents = [
    'applyAnimationAllPages',
    'applyDurationAllPages',
    'pageAllPageSetProperties',
    'pageSetProperties',
    'timelinePlay'
  ];

  constructor () {
    this.#animationPreview = null;
    this.#bridge = new EventContextBridge({
      context: 'Animation',
      supportedEvents: this.#supportedEvents
    });
  }

  async init () {
    this.#addEventListeners();
    this.#animationPreview = document.querySelector('sidebar-animation-preview');
  }

  #addEventListeners () {
    let elementsFromCurrentPage = this.getElementsFromCurrentPage();
    const updateList = () => {
      if (!this.hasAnimationConfiguration()) return;

      const updatedElementsFromCurrentPage = this.getElementsFromCurrentPage();

      if (JSON.stringify(updatedElementsFromCurrentPage) !== JSON.stringify(elementsFromCurrentPage)) {
        this.verifyDeletedElements(updatedElementsFromCurrentPage);
      }

      elementsFromCurrentPage = updatedElementsFromCurrentPage;
    };

    INKIVE.EditorFacade.on('history.change', updateList);
    INKIVE.EditorFacade.on('history.undo', updateList);
    INKIVE.EditorFacade.on('history.redo', updateList);

    INKIVE.DesignFacade.on('group.ungroup', ({ elements }) => {
      this.removeElementFromGroup(elements);
    });

    if (INKIVE.C.IS_MOBILE_APP || INKIVE.C.IS_MOBILE) {
      this.#addMobileEventListeners();
    }
  }

  #addMobileEventListeners () {
    this.#bridge.on('applyAnimationAllPages', async (data) => {
      await this.applyConfiguration('applySameAnimation', data.applyAnimationAllPages);
    });

    this.#bridge.on('applyDurationAllPages', async (data) => {
      await this.applyConfiguration('applySameDuration', data.applyDurationAllPages);
    });

    this.#bridge.on('pageAllPageSetProperties', async (configuration) => {
      const { properties, previewAnimationOnCurrentPage } = configuration;
      const selectedElements = this.getSelectedAnimatableElements();

      await this.setConfigurationToPage(this.formatBridgeProperties(properties), this.#allPages);

      if (previewAnimationOnCurrentPage) {
        const animateSelectedElements = selectedElements.length > 0;

        this.#animationPreview.playCurrentAnimationOnCurrentPage(animateSelectedElements);
      }
    });

    this.#bridge.on('pageSetProperties', async (configuration) => {
      const { designId, properties, previewAnimationOnCurrentPage } = configuration;
      const selectedElements = this.getSelectedAnimatableElements();

      await this.setConfigurationToPage(this.formatBridgeProperties(properties), [designId]);

      if (previewAnimationOnCurrentPage) {
        const animateSelectedElements = selectedElements.length > 0;

        this.#animationPreview.playCurrentAnimationOnCurrentPage(animateSelectedElements);
      }
    });

    this.#bridge.on('timelinePlay', async () => {
      this.#animationPreview.playCurrentAnimationOnCurrentPage();
    });
  }

  formatBridgeProperties (properties) {
    const { duration, ...rest } = properties;

    if (duration) {
      return { ...rest, pageDuration: duration };
    }

    return rest;
  }

  get #animationConfiguration () {
    return Store.getState().metadata.animation ?? {};
  }

  /**
   *
   * @returns {boolean}
   */
  get #isAnimated () {
    return !!Store.getState().metadata.animation;
  }

  get #allPages () {
    return Store.getState().editor.pagesOrder ?? [];
  }

  #flattenElementGroups (elements) {
    return elements.reduce((acc, element) => {
      const { id, type, elements } = INKIVE.DesignFacade.get(element?.id) ?? {};
      if (type === 'background' || !id) return acc;
      if (type === 'group') {
        return acc.concat(
          this.#flattenElementGroups(elements.map(({ id }) => INKIVE.DesignFacade.get(id)))
        );
      }

      return acc.concat({ id, type });
    }, []);
  }

  getElementsFromCurrentPage () {
    return this.#flattenElementGroups(INKIVE.DesignFacade.get());
  }

  getSelectedAnimatableElements () {
    const selectedElements = INKIVE.EditorFacade.canvas.getSelectedElems().filter(Boolean);

    return this.#flattenElementGroups(selectedElements);
  }

  getPageConfiguration (pageId = INKIVE.PageFactory.getCurrentPageId()) {
    return this.#animationConfiguration.pages?.[pageId] ?? {};
  }

  hasChanges () {
    return this.#hasChanges;
  }

  getAnimationConfiguration () {
    return this.#animationConfiguration;
  }

  hasAnimationConfiguration () {
    return Object.keys(this.#animationConfiguration).length > 0;
  }

  hasGroupAnimation (elements) {
    const pageConfiguration = this.getPageConfiguration();
    const groupAnimations = pageConfiguration.groupAnimations ?? [];

    return groupAnimations.some(({ elements: groupElements }) => {
      return groupElements.some((element) => elements.includes(element));
    });
  }

  usesSameGroupAnimation (elements) {
    const pageConfiguration = this.getPageConfiguration();
    const groupAnimations = pageConfiguration.groupAnimations ?? [];

    return groupAnimations.some(({ elements: groupElements }) => {
      const hasSameElements = groupElements.every((element) => elements.includes(element)) &&
        groupElements.length === elements.length;

      if (!hasSameElements) return false;

      return groupElements.every((element) => {
        const elementAnimation = pageConfiguration.elementAnimations?.[element];

        return elementAnimation?.animation === pageConfiguration.elementAnimations?.[elements[0]]?.animation;
      });
    });
  }

  /**
   *
   * @param {boolean} value
   */
  async #updateIsAnimatedFlag (value) {
    if (this.#isAnimated !== value) {
      await INKIVE.CompanyFactory.setProjectData({ is_animated: value });
    }
  }

  async setAnimationConfiguration (animationConfiguration) {
    const allPages = this.#allPages;
    const filteredConfiguration = Object.keys(animationConfiguration.pages ?? {})
      .reduce((acc, pageId) => {
        if (!allPages.includes(Number(pageId))) {
          return acc;
        }

        const { animation, elementAnimations } = animationConfiguration.pages[pageId];

        if (animation === undefined || animation === 'no_animation') {
          if (Object.keys(elementAnimations || {}).length === 0) {
            return acc;
          }

          if (Object.values(elementAnimations).every(animation => animation === 'no_animation')) {
            return acc;
          }
        }

        return {
          ...acc,
          [pageId]: animationConfiguration.pages[pageId]
        };
      }, {});

    if (Object.keys(filteredConfiguration).length === 0) {
      await Promise.all([this.#updateIsAnimatedFlag(false), Store.dispatch(saveMetadata({ animation: null }))]);
    } else {
      const metadata = {
        animation: {
          ...animationConfiguration,
          pages: filteredConfiguration
        }
      };
      await Promise.all([this.#updateIsAnimatedFlag(true), Store.dispatch(saveMetadata(metadata))]);
    }

    this.#hasChanges = true;
    INKIVE.hasEditorUnsavedChanges = true;
  }

  async setConfigurationToPage (configuration, pages = [INKIVE.PageFactory.getCurrentPageId()]) {
    const animationConfiguration = this.#animationConfiguration;
    const { applySameDuration, applySameAnimation, ...pageConfiguration } = configuration;

    const newConfiguration = pages.reduce((acc, pageId) => ({
      ...acc,
      pages: {
        ...acc.pages,
        [pageId]: {
          pageDuration: animationConfig.defaultPageDuration,
          inOutDuration: animationConfig.defaultInOutDuration,
          ...animationConfiguration?.pages?.[pageId],
          ...pageConfiguration
        }
      }
    }), animationConfiguration);

    newConfiguration.apply_duration_all_pages = applySameDuration === undefined ? newConfiguration.apply_duration_all_pages : applySameDuration;
    newConfiguration.apply_animation_all_pages = applySameAnimation === undefined ? newConfiguration.apply_animation_all_pages : applySameAnimation;

    await this.setAnimationConfiguration(newConfiguration);
  }

  async applyConfiguration (key, value, configuration = this.getPageConfiguration()) {
    if (!this.#allowedConfigurationKeys.includes(key)) {
      return;
    }

    const pages = configuration.applySameDuration || key === 'applySameDuration' || key === 'applySameAnimation'
      ? this.#allPages
      : [INKIVE.PageFactory.getCurrentPageId()];

    const newConfiguration = (key === 'inOutDuration' || key === 'pageDuration' || key === 'minimumPageDuration')
      ? { [key]: value * 1000 }
      : { [key]: value };

    if (key === 'applySameDuration' && value === true) {
      newConfiguration.inOutDuration = configuration.inOutDuration * 1000;
      newConfiguration.pageDuration = configuration.pageDuration * 1000;
    }

    if (key === 'applySameAnimation' && value === true) {
      const animationConfiguration = this.#animationConfiguration;
      const currentPageId = INKIVE.PageFactory.getCurrentPageId();
      newConfiguration.animation =
        animationConfiguration.pages[currentPageId].animation;
    }

    await this.setConfigurationToPage(newConfiguration, pages);
    return newConfiguration;
  }

  async setPageAnimation (animation, applyToAllPages = false) {
    const pages = applyToAllPages
      ? this.#allPages
      : [INKIVE.PageFactory.getCurrentPageId()];

    await this.setConfigurationToPage({ animation }, pages);
  }

  async setElementAnimation (elements, animation) {
    if (elements.length > 1) {
      return await this.setGroupAnimation(elements, animation);
    }

    const [element] = elements;
    const pageConfiguration = this.getPageConfiguration();
    const groupAnimations = this.removeElementsFromGroup(pageConfiguration.groupAnimations, elements);

    await this.setConfigurationToPage({
      elementAnimations: {
        ...this.getPageConfiguration()?.elementAnimations,
        [element]: animation
      },
      groupAnimations
    });
  }

  async setGroupAnimation (elements, animation) {
    const animations = elements.reduce((acc, element) => ({
      ...acc,
      [element]: animation
    }), {});
    const pageConfiguration = this.getPageConfiguration();
    const groupAnimations = this.removeElementsFromGroup(pageConfiguration.groupAnimations, elements)
      .concat({ elements });

    await this.setConfigurationToPage({
      elementAnimations: {
        ...pageConfiguration.elementAnimations,
        ...animations
      },
      groupAnimations
    });
  }

  async verifyDeletedElements (elementsInPage) {
    const pageConfiguration = this.getPageConfiguration();
    const allElementsInPage = elementsInPage.map(({ id }) => id);
    const deletedElements = Object.keys(pageConfiguration.elementAnimations ?? {}).filter(element => !allElementsInPage.includes(element));

    if (!deletedElements.length) return;

    const groupAnimations = this.removeElementsFromGroup(pageConfiguration.groupAnimations, deletedElements);
    const animationsUpdated = Object.entries(pageConfiguration.elementAnimations ?? {})
      .reduce((acc, [element, animation]) => {
        if (deletedElements.includes(element)) return acc;

        return {
          ...acc,
          [element]: animation
        };
      }, {});

    await this.setConfigurationToPage({
      elementAnimations: animationsUpdated,
      groupAnimations
    });
  }

  removeElementAnimation (elements) {
    const pageConfiguration = this.getPageConfiguration();
    const groupAnimations = this.removeElementsFromGroup(pageConfiguration.groupAnimations, elements);
    const animationsUpdated = Object.entries(pageConfiguration.elementAnimations ?? {})
      .reduce((acc, [element, animation]) => {
        if (elements.includes(element)) return acc;

        return {
          ...acc,
          [element]: animation
        };
      }, {});

    this.setConfigurationToPage({
      elementAnimations: animationsUpdated,
      groupAnimations
    });
  }

  removeElementsFromGroup (groups, elements) {
    if (!Array.isArray(groups)) return [];

    return groups.reduce((acc, next) => {
      const { elements: groupElements } = next;
      const filteredElements = groupElements.filter((element) => !elements.includes(element));

      if (filteredElements.length <= 1) {
        return acc;
      }

      return acc.concat({ elements: filteredElements });
    }, []);
  }

  removeElementFromGroup (elementId) {
    const pageConfiguration = this.getPageConfiguration();
    const groupAnimations = this.removeElementsFromGroup(pageConfiguration.groupAnimations, elementId);

    this.setConfigurationToPage({
      groupAnimations
    });
  }

  /**
   *
   * @param sourcePageId {number}
   * @param destinationPageId {number}
   */
  copyPageConfiguration (sourcePageId, destinationPageId) {
    const sourceConfiguration = this.getPageConfiguration(sourcePageId);

    this.setConfigurationToPage(sourceConfiguration, [destinationPageId]);
  }
}
